import { Col, Form, Input, Row, Select } from 'antd';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NAMESPACE } from '@/constants';
import { FORM_FIELD } from './LineItem.constants';
import { FORM_FIELD as COMMERCIAL_FIELD } from '../Commercial/Commercial.constants';
import { useChannelOptions } from '../../hooks/useChannelOptions';
import { useGetCalculatorPackageByIdQuery } from '@/api/calculatorPackages';
import { withSelectOption } from '../../../../utils/normalizeChannelFormPayload';

const { Item } = Form;
const { Option } = Select;

const LineItem: FC = () => {
  const form = Form.useFormInstance();
  const rateSource = Form.useWatch(COMMERCIAL_FIELD.RATE_SOURCE, form) as
    | 'MANUAL'
    | 'PACKAGE'
    | 'TECH_FEE'
    | undefined;
  const packageId = Form.useWatch(COMMERCIAL_FIELD.PACKAGE_ID, form) as string | undefined;
  const isPackageMode = rateSource === 'PACKAGE';
  const isTechFeeMode = rateSource === 'TECH_FEE';
  const selectedFormats = Form.useWatch(FORM_FIELD.FORMATS, form) as string[] | undefined;
  const { options, areOptionsLoading } = useChannelOptions();
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);

  const { data: packageData } = useGetCalculatorPackageByIdQuery(packageId ?? '', {
    skip: !isPackageMode || !packageId
  });

  const formatOptions = useMemo(() => {
    const fromApi = (options?.formats ?? []) as string[];
    const current = Array.isArray(selectedFormats) ? selectedFormats : [];
    return withSelectOption(fromApi, current[0]).concat(
      current.slice(1).filter((f) => !fromApi.includes(f))
    );
  }, [options?.formats, selectedFormats]);

  const packageFormats = useMemo(
    () =>
      isPackageMode && packageData
        ? (packageData as any)?.format
          ? (packageData.format as string)
              .split(',')
              .map((f) => f.trim())
              .filter(Boolean)
          : [
              ...new Set(
                ((packageData as any)?.rows?.map((r: any) => r?.format).filter(Boolean) ??
                  []) as string[]
              )
            ]
        : [],
    [isPackageMode, packageData]
  );

  /**
   * Keeps the formats field in step with the package that defines it.
   *
   * The field is disabled in package mode — the package decides the formats —
   * but only its options came from the package, not its value. So formats left
   * over from an earlier package or a manual rate stayed displayed in a field
   * nobody could edit: a CTV package offering only 20" would show 10" and 30"
   * with no way to correct them.
   *
   * Anything the package does not offer is dropped; if that leaves nothing,
   * the package's own formats are taken wholesale, which is what a disabled
   * field should have been showing all along.
   */
  useEffect(() => {
    if (!isPackageMode || !packageFormats.length) return;

    const current = Array.isArray(selectedFormats) ? selectedFormats : [];
    const valid = current.filter((format) => packageFormats.includes(format));
    const next = valid.length ? valid : packageFormats;

    // Compared before writing, or this would loop on every render.
    const same = next.length === current.length && next.every((f, i) => f === current[i]);
    if (!same) form.setFieldsValue({ [FORM_FIELD.FORMATS]: next });
  }, [form, isPackageMode, packageFormats, selectedFormats]);

  if (isTechFeeMode) {
    return (
      <>
        <Row gutter={24}>
          <Col span={24}>
            <Item name={FORM_FIELD.FORMATS} label={t('format')} initialValue={['Tech Fee']}>
              <Select mode="multiple" disabled size="large" options={[{ value: 'Tech Fee', label: 'Tech Fee' }]} />
            </Item>
          </Col>
        </Row>
        <Form.Item name={FORM_FIELD.SKYRISE_POWERED} hidden initialValue={false}>
          <Input type="hidden" />
        </Form.Item>
      </>
    );
  }

  return (
    <>
      <Row gutter={24}>
        <Col span={24}>
          <Item
            rules={[{ required: true, message: t('requiredFieldMessage', { ns: NAMESPACE.COMMON }) }]}
            name={FORM_FIELD.FORMATS}
            label={t('format')}
          >
            {isPackageMode ? (
              <Select
                mode="multiple"
                disabled
                size="large"
                placeholder={t('selectFormat')}
                options={packageFormats.map((f) => ({ value: f, label: f }))}
              />
            ) : (
              <Select loading={areOptionsLoading} mode="multiple" size="large" placeholder={t('selectFormat')}>
                {formatOptions.map((format) => (
                  <Option key={format} value={format}>
                    {format}
                  </Option>
                ))}
              </Select>
            )}
          </Item>
        </Col>
      </Row>
      <Form.Item name={FORM_FIELD.SKYRISE_POWERED} hidden initialValue={false}>
        <Input type="hidden" />
      </Form.Item>
    </>
  );
};

export default LineItem;
